import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Ticket status.
 *
 * Perechile vechi: `settings/easeedesk/ticket_status_settings`,
 * `get_status_list`, `ticket_status_details`, `create_new_status`,
 * `update_status`, `delete_status`, `separate_status`, `update_status_effort`,
 * `change_status_status`, `slaCategory`, `set_status_label`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface StatusRow {
  ID: number
  status_name: string
  status_color: string
  status_type: number
  status_type_name: string
  type_color: string
  record_sla: number
  hidden: number
  manager_permission: number
  /** 1 = rand comun tuturor instantelor; nu se poate redenumi. */
  default: number
  scheme_id: number
  active: number
  estimated_effort: number
  sla_category: number
  /** Numele sub care il vede furnizorul asta; gol = numele comun. */
  label: string
  /** Tipurile de tichet care trec prin status. */
  used_by: string[]
}

export interface StatusesPage {
  default_status_list: StatusRow[]
  custom_status_list: StatusRow[]
}

export interface StatusesShell {
  title: string
  moduleUrl: string
  status_types: { ID: number; status_type_name: string; type_color: string }[]
}

export interface StatusDetails {
  ID: number
  status_name: string
  status_color: string
  status_type: number
  record_sla: number
  hidden: number
  manager_permission: number
  default: number
  used_by: string[]
}

export interface StatusFlow {
  workflow_id: number
  name: string
  types: string[]
}

/** Raspunsul lui `update` cand redenumirea atinge si pe altii. */
export interface StatusRenameConfirm {
  confirm: true
  old_name: string
  new_name: string
  used_by: string[]
  flows: StatusFlow[]
}

export interface StatusFields {
  status_name: string
  status_color: string
  status_type: number
  record_sla: boolean
  hidden: boolean
  manager_permission: boolean
}

export function statusesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_status'],
    queryFn: () => apiGet<StatusesShell>('/settings/easeedesk/ticket_status_settings'),
  })
}

export function statusesListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_status', 'list'],
    queryFn: () => apiGet<StatusesPage>('/settings/easeedesk/ticket_status_settings/list'),
  })
}

export function statusDetailsQuery(status: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_status', 'one', status],
    queryFn: () => apiGet<StatusDetails>(`/settings/easeedesk/ticket_status_settings/${status}`),
    enabled: status > 0,
  })
}

function fieldsBody(fields: StatusFields) {
  return {
    status_name: fields.status_name,
    status_color: fields.status_color,
    status_type: fields.status_type,
    record_sla: fields.record_sla ? '1' : '0',
    hidden: fields.hidden ? '1' : '0',
    manager_permission: fields.manager_permission ? '1' : '0',
  }
}

export function createStatus(fields: StatusFields) {
  return apiPost<{ ok?: boolean; ID?: number }>('/settings/easeedesk/ticket_status_settings', fieldsBody(fields))
}

export function updateStatus(status: number, fields: StatusFields, confirmed = false) {
  return apiPost<{ ok?: boolean } & Partial<StatusRenameConfirm>>(
    `/settings/easeedesk/ticket_status_settings/${status}`,
    { ...fieldsBody(fields), confirmed: confirmed ? '1' : '0' },
  )
}

export function separateStatus(status: number, fields: StatusFields, flows: number[]) {
  return apiPost<{ ok?: boolean; status_id?: number; moved?: Record<string, number> }>(
    `/settings/easeedesk/ticket_status_settings/${status}/separate`,
    { ...fieldsBody(fields), flows },
  )
}

export function changeStatusActive(status: number, on: boolean) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_status_settings/${status}/status`, {
    status_status: on ? 'on' : 'off',
  })
}

export function setStatusEffort(status: number, estimated: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_status_settings/${status}/effort`, { estimated })
}

export function setStatusSlaCategory(status: number, sla_category: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_status_settings/${status}/sla_category`, {
    sla_category,
  })
}

export function setStatusLabel(status: number, label: string) {
  return apiPost<{ ok?: boolean; label?: string }>(`/settings/easeedesk/ticket_status_settings/${status}/label`, {
    label,
  })
}

/** Se sterge randul de SCHEMA al furnizorului, nu statusul comun. */
export function removeStatusScheme(scheme: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/ticket_status_settings/scheme/${scheme}/remove`, {})
}
